import { GridInitialStatePro } from "@mui/x-data-grid-pro/models/gridStatePro";
import { isBuiltInField } from "./builtInGridFields";
import { DiagnosticView, ViewOverride } from "@/models/DiagnosticView";

function safeParse<T extends object>(input: string | undefined): T {
  if (!input) return {} as T;
  try {
    const parsed = JSON.parse(input);
    return (typeof parsed === "object" && parsed !== null ? parsed : {}) as T;
  } catch {
    return {} as T;
  }
}

export function mergeGridState(
  view: DiagnosticView,
  override: ViewOverride | null,
): GridInitialStatePro {
  const base = safeParse<GridInitialStatePro>(view.gridState);
  if (!override) return base;

  const delta = safeParse<GridInitialStatePro>(override.gridStateDelta);
  if (Object.keys(delta).length === 0) return base;

  const result: GridInitialStatePro = structuredClone(base);
  const viewColumnIds = view.columns.map(c => c.diagnosticId);
  const validIds = new Set(viewColumnIds);

  // 1. Column order
  const adminOrder = base.columns?.orderedFields ?? viewColumnIds;
  const userOrder = delta.columns?.orderedFields ?? [];
  if (userOrder.length > 0) {
    const removedByAdmin = userOrder.filter(c => !adminOrder.includes(c) && !isBuiltInField(c));
    const newInAdmin = adminOrder.filter(c => !userOrder.includes(c));
    let merged = userOrder.filter(c => !removedByAdmin.includes(c));
    for (const col of newInAdmin) {
      const adminIdx = adminOrder.indexOf(col);
      // Find the nearest left neighbour in adminOrder that is present in merged
      let insertAt = 0;
      for (let i = adminIdx - 1; i >= 0; i--) {
        const neighbourIdx = merged.indexOf(adminOrder[i]);
        if (neighbourIdx !== -1) {
          insertAt = neighbourIdx + 1;
          break;
        }
      }
      merged.splice(insertAt, 0, col);
    }
    merged = merged.filter(c => validIds.has(c) || isBuiltInField(c));
    result.columns = { ...result.columns, orderedFields: merged };
  }

  // 2. Widths — user wins per column
  if (delta.columns?.dimensions) {
    result.columns = {
      ...result.columns,
      dimensions: { ...base.columns?.dimensions, ...delta.columns.dimensions },
    };
  }

  // 3. Visibility — user wins per column
  if (delta.columns?.columnVisibilityModel) {
    result.columns = {
      ...result.columns,
      columnVisibilityModel: {
        ...base.columns?.columnVisibilityModel,
        ...delta.columns.columnVisibilityModel,
      },
    };
  }

  // 4. Sort / pinned — user wins entire model
  if (delta.sorting?.sortModel) result.sorting = delta.sorting;
  if (delta.pinnedColumns) result.pinnedColumns = delta.pinnedColumns;

  // 5. Filter — user only
  if (delta.filter) result.filter = delta.filter;

  return result;
}
